<template>
  <KekkleCard class="mb-4">
    <!-- Header with Back Button -->
    <div class="mb-6 flex items-center">
      <button
        v-if="currentStep !== 'initial'"
        class="tex mr-3 rounded-lg p-2 dark:text-slate-200"
        @click="goBack"
      >
        <span class="text-2xl">←</span>
      </button>
      <h2 class="text-xl font-bold dark:text-slate-200">
        {{ currentTitle }}
      </h2>
    </div>

    <!-- Step: Initial - Select existing group or choose action -->
    <div v-if="currentStep === 'initial'">
      <!-- Show existing groups if any -->
      <div v-if="groups && groups.length !== 0" class="w-100 mb-6">
        <p class="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {{ $t("pages.createselectgroup.select_group") }}
        </p>
        <div class="">
          <div
            v-for="group in groups"
            :key="group._id"
            class="mb-2 cursor-pointer rounded border-2 border-black-dark bg-white p-4 shadow-purple-hard-sm transition-all hover:translate-x-1 hover:shadow-none dark:bg-kekkle-dark-light"
            @click="selectGroup(group._id)"
          >
            <p class="text-center font-medium dark:text-slate-200">
              {{ group.name }}
            </p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col gap-3">
        <default-button class="w-full" @click="currentStep = 'create'">
          <p>✨ {{ $t("pages.createselectgroup.make_group_question") }}</p>
        </default-button>
        <default-button class="w-full" @click="currentStep = 'join'">
          <p>🔗 {{ $t("global.inputs.labels.join_group") }}</p>
        </default-button>
      </div>
    </div>

    <!-- Step: Create New Group -->
    <div v-else-if="currentStep === 'create'">
      <p class="mb-4 dark:text-slate-200">
        {{ $t("pages.createselectgroup.only_name_needed") }}
      </p>
      <text-input
        v-model="groupName"
        class="my-4"
        :placeholder="$t('global.inputs.placeholders.group_name')"
        :label="$t('global.inputs.labels.group_name') + '*'"
        :has-error="!nameValid"
        :error-message="$t('global.inputs.errors.group_name_karakters')"
      >
      </text-input>

      <default-button
        :disabled="!nameValid"
        :loading="status.submitting"
        class="w-full"
        @click="saveGroup()"
        >{{ $t("global.inputs.labels.make_group") }}</default-button
      >
    </div>

    <!-- Step: Join Existing Group -->
    <div v-else-if="currentStep === 'join'">
      <p class="mb-4 dark:text-slate-200">
        {{ $t("pages.createselectgroup.join_existing_group") }}
      </p>
      <text-input
        v-model="joinToken"
        class="my-4"
        :placeholder="$t('global.inputs.placeholders.group_token')"
        :label="$t('global.inputs.labels.group_token') + '*'"
        :has-error="!tokenValid"
        :error-message="$t('global.inputs.errors.group_token')"
      >
      </text-input>
      <default-button
        :disabled="!tokenValid || status.submitting"
        class="w-full"
        @click="joinGroup()"
        >{{ $t("global.inputs.labels.join_group") }}</default-button
      >
    </div>

    <InformationText
      v-if="status.error"
      color="red"
      class="mt-4"
      :information="status.errorMessage"
    ></InformationText>
  </KekkleCard>

  <default-button color="red" @click="logout()">{{
    $t("global.inputs.placeholders.logout")
  }}</default-button>

  <p
    v-if="(!groups || groups.length === 0) && !status.fetchingGroups"
    class="mt-8 rounded-lg border-2 border-kekkle-dark bg-kekkle-purple-light p-2 text-center font-medium text-kekkle-dark shadow-purple-hard-sm"
    @click="$router.push({ name: 'DeleteAccount' })"
  >
    ⚠️ {{ $t("auth.want_to_delete_account") }} ⚠️
  </p>
</template>
<script>
import { firebaseFunctions } from "@/helpers/firebase/firebase";
import { httpsCallable } from "firebase/functions";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";

import TextInput from "../components/Inputs/TextInput.vue";
import router from "@/router/router";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import KekkleCard from "@/components/ui/KekkleCard.vue";
import InformationText from "@/components/InformationText.vue";
import * as Sentry from "@sentry/vue";

export default {
  name: "CreateSelectGroup",
  components: { TextInput, DefaultButton, KekkleCard, InformationText },
  setup() {
    const authStore = useAuthStore();
    const groupsStore = useGroupsStore();
    const userStore = useUserStore();
    return { authStore, groupsStore, userStore };
  },
  data: function () {
    return {
      status: {
        submitting: false,
        error: false,
        errorMessage: "",
        fetchingGroups: false,
      },
      groupName: "",
      joinToken: "",
      currentStep: "initial", // 'initial', 'create', 'join'
    };
  },

  computed: {
    groups() {
      return Array.from(this.groupsStore.userGroups.values());
    },
    tokenValid() {
      return this.joinToken && this.joinToken.length >= 6;
    },
    nameValid() {
      return (
        this.groupName &&
        this.groupName.length < 100 &&
        this.groupName.length > 2
      );
    },
    currentTitle() {
      if (this.currentStep === "create") {
        return this.$t("pages.createselectgroup.make_group_question");
      } else if (this.currentStep === "join") {
        return this.$t("global.inputs.labels.join_group");
      }
      // Show different title based on whether user has groups
      if (this.groups && this.groups.length > 0) {
        return this.$t("pages.createselectgroup.select_group");
      }
      return this.$t("pages.createselectgroup.no_selected_group");
    },
  },
  async created() {
    if (window.localStorage.getItem("joinToken")) {
      let joinToken = window.localStorage.getItem("joinToken");
      //reset token;
      window.localStorage.removeItem("joinToken");
      await this.$router.push({
        name: "InviteLinkFollowup",
        query: { token: joinToken },
      });
    } else {
      this.status.fetchingGroups = true;
      await this.groupsStore.refreshAllUserGroupsData();
      await this.groupsStore.refreshAllUserGroupsData();
      this.status.fetchingGroups = false;
    }
  },
  methods: {
    router() {
      return router;
    },
    logout() {
      this.authStore.logout();
    },
    goBack() {
      this.currentStep = "initial";
      this.status.error = false;
      this.status.errorMessage = "";
    },
    async selectGroup(groupId) {
      try {
        await this.userStore.switchActiveGroup(groupId);
        await this.groupsStore.refreshAllUserGroupsData();
        await this.$router.push({ name: "Group" });
      } catch (error) {
        Sentry.captureException(error);
        this.status.error = true;
        this.status.errorMessage = this.$t(
          "pages.createselectgroup.switch_group_failed",
        );
      }
    },
    async joinGroup() {
      await this.$router.push({
        name: "InviteLinkFollowup",
        query: { token: this.joinToken },
      });
    },
    async saveGroup() {
      this.status.error = false;
      this.status.errorMessage = "";
      if (this.nameValid) {
        this.status.submitting = true;
        try {
          const createGroupFunction = await httpsCallable(
            firebaseFunctions,
            "createGroup",
          );
          await createGroupFunction({ name: this.groupName });
          await this.authStore.refreshAuthIdToken();
          await this.userStore.refreshUserData();
          await this.groupsStore.refreshAllUserGroupsData();

          this.$router.push({ name: "Group" });
          this.groupName = "";
        } catch (error) {
          Sentry.captureException(error);
          this.status.error = true;
          this.status.errorMessage = this.$t(
            "pages.createselectgroup.create_group_failed",
          );
        } finally {
          this.status.submitting = false;
        }
      }
    },
  },
};
</script>
